import { useState, useRef, useCallback, useEffect } from "react";
import { ArrowsCounterClockwise, Spinner } from "@phosphor-icons/react";
import useComparisonImages from "./useComparisonImages";

// ── SVG annotation circles for a single panel ────────────────────────────────
function PanelAnnotations({ annotations }) {
  if (!annotations || !annotations.length) return null;
  return (
    <svg
      viewBox="0 0 1000 750"
      className="absolute inset-0 w-full h-full"
      style={{ pointerEvents: "none", zIndex: 10 }}
    >
      {annotations.map((ann, i) => {
        const cx = ann.x * 1000;
        const cy = ann.y * 750;
        const r = ann.radius * 1000;
        const color = ann.color === "red" ? "#ef4444"
          : ann.color === "orange" ? "#f97316"
          : ann.color === "green" ? "#22c55e"
          : ann.color === "blue" ? "#3b82f6"
          : ann.color === "purple" ? "#a855f7"
          : "#ef4444";
        return (
          <g key={i}>
            <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={2} opacity={0.85} />
            <circle cx={cx} cy={cy} r={Math.min(r * 0.38, 14)} fill={color} opacity={0.9} />
            <text x={cx} y={cy + 0.5} textAnchor="middle" dominantBaseline="middle"
              fontSize={Math.min(r * 0.36, 12)} fontWeight="bold" fill="white"
              style={{ pointerEvents: "none" }}>
              {ann.index + 1}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/**
 * Side-by-side comparison viewer with synchronized pan/zoom.
 */
export default function SideBySideViewer({ sessionId, changes }) {
  const { latestImage, referenceImage, loading, error } = useComparisonImages(sessionId);

  // Shared transform state
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const translateStart = useRef({ x: 0, y: 0 });

  const reset = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  // Wheel zoom (synchronized)
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    setScale((prev) => {
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      return Math.min(Math.max(prev * delta, 0.5), 5);
    });
  }, []);

  // Mouse drag (synchronized)
  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY };
    translateStart.current = { ...translate };
  }, [translate]);

  const handleMouseMove = useCallback((e) => {
    if (!dragging) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setTranslate({
      x: translateStart.current.x + dx,
      y: translateStart.current.y + dy,
    });
  }, [dragging]);

  const handleMouseUp = useCallback(() => {
    setDragging(false);
  }, []);

  useEffect(() => {
    if (dragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [dragging, handleMouseMove, handleMouseUp]);

  // Build annotation arrays
  const referenceAnnotations = (changes || [])
    .map((c, idx) => c.reference_annotation ? { ...c.reference_annotation, index: idx } : null)
    .filter(Boolean);
  const latestAnnotations = (changes || [])
    .map((c, idx) => c.latest_annotation ? { ...c.latest_annotation, index: idx } : null)
    .filter(Boolean);

  const transformStyle = {
    transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
    transformOrigin: "center center",
    transition: dragging ? "none" : "transform 0.1s ease-out",
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400">
        <Spinner size={24} className="animate-spin mr-2" />
        <span className="text-sm">Loading full-resolution images...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-16 text-red-500 text-sm">
        Failed to load images: {error}
      </div>
    );
  }

  return (
    <div className="border-b border-slate-200" data-testid="side-by-side-viewer">
      {/* Toolbar */}
      <div className="px-3 py-2 flex items-center justify-between bg-primary/5 border-b border-primary/10">
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">
            Zoom: {Math.round(scale * 100)}% · Scroll to zoom, drag to pan
          </span>
        </div>
        <button
          onClick={reset}
          className="flex items-center gap-1 px-2 py-1 rounded text-xs text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors border border-slate-200"
          data-testid="side-by-side-reset"
        >
          <ArrowsCounterClockwise size={13} />
          Reset
        </button>
      </div>

      {/* Panels */}
      <div className="grid grid-cols-2 divide-x divide-slate-200">
        {/* Reference (left) */}
        <div className="relative overflow-hidden bg-slate-100" style={{ minHeight: 300 }}>
          <div className="absolute top-2 left-2 z-20 px-2 py-0.5 bg-slate-800/70 text-white text-xs rounded">
            Reference (Older)
          </div>
          <div
            className="w-full h-full flex items-center justify-center select-none"
            style={{ cursor: dragging ? "grabbing" : "grab", minHeight: 300 }}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
          >
            <div className="relative" style={transformStyle}>
              {referenceImage ? (
                <img
                  src={`data:image/jpeg;base64,${referenceImage}`}
                  alt="Reference drawing"
                  className="max-w-full max-h-80 object-contain"
                  draggable={false}
                />
              ) : (
                <div className="w-64 h-48 flex items-center justify-center text-slate-400 text-xs">
                  No image available
                </div>
              )}
              <PanelAnnotations annotations={referenceAnnotations} />
            </div>
          </div>
        </div>

        {/* Latest (right) */}
        <div className="relative overflow-hidden bg-slate-100" style={{ minHeight: 300 }}>
          <div className="absolute top-2 left-2 z-20 px-2 py-0.5 bg-primary/80 text-white text-xs rounded">
            Latest (Newer)
          </div>
          <div
            className="w-full h-full flex items-center justify-center select-none"
            style={{ cursor: dragging ? "grabbing" : "grab", minHeight: 300 }}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
          >
            <div className="relative" style={transformStyle}>
              {latestImage ? (
                <img
                  src={`data:image/jpeg;base64,${latestImage}`}
                  alt="Latest drawing"
                  className="max-w-full max-h-80 object-contain"
                  draggable={false}
                />
              ) : (
                <div className="w-64 h-48 flex items-center justify-center text-slate-400 text-xs">
                  No image available
                </div>
              )}
              <PanelAnnotations annotations={latestAnnotations} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
